import { type MarketsResponse } from '../lib/api.js';
import { fmtInt, fmtPct } from '../lib/format.js';
import { ChartCard } from './charts/ChartCard.js';
import { KpiCard } from './KpiCard.js';

/**
 * Detalle por RATE CODE del Reservation Statistics 2 (`diamond_detalle`).
 *
 * Es el mismo reporte del que sale Diamond: la ingesta guardaba todos los
 * códigos desde el principio y hasta ahora solo se leía Diamond. Aquí se ve qué
 * produjo cada código y cuánto pesa Diamond dentro del total vendido.
 *
 * El Grand Total NO aparece como fila: es el total impreso del reporte y se usa
 * solo para cuadrar (`descuadres`), nunca como un código más.
 */
export function MarketsPanel({ data }: { data: MarketsResponse }) {
  if (data.sinDatos) {
    return (
      <ChartCard title="Rate codes (Reservation Statistics 2)">
        <p className="p-4 text-sm text-slate-500">
          Sin detalle por rate code en el rango. Se alimenta del mismo Reservation Statistics 2 del
          que sale Diamond: sube uno en Ingesta y el desglose aparece aquí.
        </p>
      </ChartCard>
    );
  }

  const max = data.porMarket[0]?.rooms ?? 0;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
        Rate codes · {data.from} a {data.to}
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Noches vendidas" kpi={{ value: data.total }} format={fmtInt} />
        <KpiCard label="Noches Diamond" kpi={{ value: data.diamond }} format={fmtInt} />
        <KpiCard
          label="% Diamond del total"
          kpi={{ value: data.diamondShare == null ? null : data.diamondShare * 100 }}
          format={fmtPct}
        />
        <KpiCard
          label={`Código líder · ${data.lider?.market ?? '—'}`}
          kpi={data.cards.nochesMarketLider}
          format={fmtInt}
          hint="Comparado con ESE MISMO código un año antes, no con el líder de aquel año."
        />
      </div>

      {/* Un descuadre es siempre un fallo de lectura del PDF, nunca del hotel:
          el propio reporte imprime sus códigos y su Grand Total. */}
      {data.descuadres.length > 0 && (
        <p className="rounded-lg bg-amber-950/50 px-3 py-2 text-xs text-amber-300">
          {data.descuadres.length} día(s) donde los códigos no suman el Grand Total impreso:{' '}
          {data.descuadres
            .slice(0, 5)
            .map((d) => `${d.fecha} (${fmtInt(d.suma)} vs ${fmtInt(d.grandTotal)})`)
            .join(' · ')}
          {data.descuadres.length > 5 && ' …'}. Es un fallo de lectura del reporte, no del hotel.
        </p>
      )}

      <ChartCard title={`Detalle por código (${fmtInt(data.total)} noches en ${data.dias} día/s)`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Rate code</th>
                <th className="px-3 py-2 text-right">Noches</th>
                <th className="px-3 py-2 text-right">% del total</th>
                <th className="px-3 py-2">Peso</th>
              </tr>
            </thead>
            <tbody>
              {data.porMarket.map((m) => (
                <tr key={m.market} className="border-t border-slate-700/60">
                  <td className="px-3 py-1.5 text-slate-200">
                    {m.market}
                    {m.esDiamond && (
                      <span className="ml-2 rounded bg-blue-900 px-1.5 py-0.5 text-[10px] text-blue-200">
                        Diamond
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                    {fmtInt(m.rooms)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                    {m.share == null ? '—' : fmtPct(m.share * 100)}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="h-2 w-full rounded bg-slate-800">
                      <div
                        className={`h-2 rounded ${m.esDiamond ? 'bg-blue-500' : 'bg-slate-500'}`}
                        style={{ width: max > 0 ? `${(m.rooms / max) * 100}%` : '0%' }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </section>
  );
}
